import React, { useState, useEffect } from "react";
import { formActivity } from '../../../realtime/formActivity';
import { useSelector, useDispatch } from "react-redux";
import { selectUserRoles } from "../../../redux/slices/userSlice";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { selectJobChangeOrderDetails, selectJobId, selectChangeOrderParent } from "../../../redux/selectors";
import { Container, Row, Col, Modal, Button } from 'react-bootstrap';
import ViewChangeOrder from './ViewChangeOrder'; // Import the ViewChangeOrder component
import { deleteChangeOrderItem } from "../../../redux/slices/changeOrderSlice";
import axiosInstance from "../../../axios/axiosConfig";
import { formatDateForEmail } from "../../../utils/dateUtils";

const ChangeOrders = ({ onItemDeleted, deletedItemsIds }) => {
    const jobId = useSelector(selectJobId);
    const userRoles = useSelector(selectUserRoles);
    const roles = mapUserRoles(userRoles);
    const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
    const changeOrderParent = useSelector(selectChangeOrderParent);
    const dispatch = useDispatch();

    const [filteredChangeOrders, setFilteredChangeOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    useEffect(() => {
        const filtered = changeOrderParent.filter(order => order.ProjectID === jobId);
        filtered.sort((a, b) => b.ChangeOrderNumber - a.ChangeOrderNumber); // Sort by ChangeOrderNumber
        setFilteredChangeOrders(filtered);
    }, [changeOrderParent, jobId]);

    const getChangeOrderCount = (changeOrderNumber) => {
        // Filter out deleted items and ensure only counting items for this job
        const detailsToCount = changeOrderDetails.filter(
            detail => detail.ProjectID === jobId &&
            detail.ChangeOrderNumber === changeOrderNumber &&
            (!deletedItemsIds || !deletedItemsIds.has(detail.ID))
        );
        return detailsToCount.length;
    };

    const handleDelete = async (id) => {
        const finishRealtimeWrite = formActivity.saving();
        try {
            // Delete parent change order
            await axiosInstance.delete(`${process.env.REACT_APP_API_URL}/change-order-parent/`, {
                data: { ID: id }
            });

            // Update local state
            setFilteredChangeOrders(prevOrders => prevOrders.filter(order => order.ID !== id));

            // Get all details associated with this change order
            const selectedOrder = filteredChangeOrders.find(order => order.ID === id);
            if (selectedOrder) {
                const orderNumber = selectedOrder.ChangeOrderNumber;

                // Find all details with this change order number
                const detailsToDelete = changeOrderDetails.filter(
                    detail => detail.ChangeOrderNumber === orderNumber
                );

                // Delete all associated details
                for (const detail of detailsToDelete) {
                    try {
                        // Try to delete using our specialized thunk
                        await dispatch(deleteChangeOrderItem(detail.ID)).unwrap();

                        // Track the deleted item ID
                        if (onItemDeleted) {
                            onItemDeleted(detail.ID);
                        }
                    } catch (error) {
                        console.error(`Error deleting change order detail ${detail.ID}:`, error);

                        // Even if there's an error, if it's because the item was not found or already deleted,
                        // we should still track it as deleted
                        if (error?.message?.includes('not found') ||
                            !error ||
                            (error && typeof error === 'object' && Object.keys(error).length === 0)) {
                            if (onItemDeleted) {
                                onItemDeleted(detail.ID);
                            }
                        }
                    }
                }
            }

        } catch (error) {
            console.error("Failed to delete change order:", error);
            alert("Failed to delete the change order. Please try again.");
        } finally { finishRealtimeWrite(); }
    };

    const handleView = (order) => {
        setSelectedOrder(order);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedOrder(null);
    };

    const formatDate = (dateString) => {
        return formatDateForEmail(dateString);
    };

    return (
        <Container className="change-order-orders">
            <h1>Change Orders</h1>
            {filteredChangeOrders.length === 0 ? (
                <p>No Change Orders</p>
            ) : (
                filteredChangeOrders.map((order, index) => (
                    <div key={index} className="mb-3 change-order-item change-order-summary-card" onClick={() => handleView(order)}>
                        <Row className="change-order-summary-header">
                            <Col>
                                <strong>Change Order #{order.ChangeOrderNumber} - {formatDate(order.DateSubmitted)}</strong>
                            </Col>
                        </Row>
                        <Row className="change-order-summary-meta">
                            <Col className="d-flex justify-content-between change-order-summary-meta-content">
                                <span>Written By: {order.WrittenBy}</span>
                                <span>Changes: {getChangeOrderCount(order.ChangeOrderNumber)}</span>
                            </Col>
                        </Row>
                        <Row className="change-order-summary-actions-row">
                            <Col className="change-order-summary-actions">
                                {roles.admin && (
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(order.ID)}>X</Button>
                                )}
                                <Button variant="primary" size="sm" onClick={() => handleView(order)}>View</Button>
                            </Col>
                        </Row>
                    </div>
                ))
            )}
            <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg" className="change-order-modal change-order-view-modal">
                <Modal.Header closeButton>
                    <Modal.Title>View Change Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && <ViewChangeOrder changeOrderId={selectedOrder.ID} notes={selectedOrder.Notes} deletedItemsIds={deletedItemsIds} />}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseViewModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ChangeOrders;
